import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { RotateCw as RefreshIcon, Clock as ScheduleIcon } from 'lucide-react';
import { getFax, retryFax } from '../../utils/faxApi';

const TERMINAL_STATES = ['delivered', 'failed', 'canceled'];
const POLL_INTERVAL_MS = 5000;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Status badge and timeline for a single fax transmission.
 * Polls every 5s until status is terminal; shows retry for failed.
 */
export default function FaxStatusPanel({ faxId, onRetrySuccess }) {
  const [fax, setFax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const fetchFax = useCallback(async () => {
    if (!faxId) return;
    try {
      setError(null);
      const data = await getFax(faxId);
      setFax(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load fax status');
      return null;
    } finally {
      setLoading(false);
    }
  }, [faxId]);

  useEffect(() => {
    fetchFax();
  }, [fetchFax]);

  useEffect(() => {
    if (!faxId || !fax) return;
    if (TERMINAL_STATES.includes(fax.status)) return;
    const timer = setInterval(fetchFax, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [faxId, fax?.status, fetchFax]);

  const handleRetry = async () => {
    if (!faxId) return;
    setRetrying(true);
    setError(null);
    try {
      const updated = await retryFax(faxId);
      setFax(updated);
      onRetrySuccess?.(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  if (loading && !fax) {
    return (
      <Card variant="outlined">
        <CardContent>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading fax status…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error && !fax) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (!fax) return null;

  const statusColor = {
    draft: 'default',
    queued: 'info',
    sending: 'info',
    delivered: 'success',
    failed: 'error',
    canceled: 'default',
  }[fax.status] || 'default';

  const timeline = [
    { label: 'Queued', at: fax.queued_at },
    { label: 'Sent', at: fax.sent_at },
    { label: 'Delivered', at: fax.delivered_at },
    { label: 'Failed', at: fax.failed_at },
  ].filter((t) => t.at);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1">Fax to {fax.to_number}</Typography>
          <Chip label={fax.status} color={statusColor} size="small" />
        </Box>
        {fax.last_error_message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fax.last_error_message}
          </Alert>
        )}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Timeline
        </Typography>
        <List dense disablePadding>
          {timeline.map((t) => (
            <ListItem key={t.label} disablePadding sx={{ py: 0 }}>
              <ListItemText
                primary={t.label}
                secondary={formatDate(t.at)}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
          {timeline.length === 0 && (
            <ListItem disablePadding>
              <ListItemText secondary={formatDate(fax.queued_at) || '—'} secondaryTypographyProps={{ variant: 'caption' }} />
            </ListItem>
          )}
        </List>
        {fax.status === 'failed' && (
          <Button
            variant="outlined"
            size="small"
            startIcon={retrying ? <LinearProgress size={16} /> : <RefreshIcon size={16} />}
            onClick={handleRetry}
            disabled={retrying}
            sx={{ mt: 2 }}
          >
            {retrying ? 'Retrying…' : 'Retry fax'}
          </Button>
        )}
        {!TERMINAL_STATES.includes(fax.status) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <ScheduleIcon size={16} />
            <Typography variant="caption" color="text.secondary">
              Polling every 5s until complete
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
